import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface ActivityLog {
  id: number;
  user_id: string | null;
  username: string | null;
  user_email: string | null;
  ip_address: string | null;
  action: string;
  details: string | null;
  created_at: string;
}

const ACTION_LABELS: Record<string, string> = {
  login: 'Login',
  logout: 'Logout',
  create_tape: 'Create Tape',
  update_tape: 'Update Tape',
  delete_tape: 'Delete Tape',
  delete_tapes_batch: 'Batch Delete',
  replace_tape: 'Mark Replaced',
  undo_replacement: 'Undo Replacement',
  flag_replacement: 'Flag Replacement',
  flag_damaged: 'Flag Damaged',
  restore_tape: 'Restore Tape',
  request_tapes: 'Request Tapes',
  fulfill_request: 'Fulfill Request',
  cancel_request: 'Cancel Request',
  create_account: 'Create Account',
  update_account: 'Update Account',
  reset_password: 'Reset Password',
  set_account_status: 'Change Status',
  delete_account: 'Delete Account',
};

const ACTION_COLORS: Record<string, string> = {
  login: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  logout: 'bg-slate-100 text-slate-600 border-slate-200',
  create_tape: 'bg-blue-50 text-blue-700 border-blue-100',
  update_tape: 'bg-amber-50 text-amber-700 border-amber-100',
  delete_tape: 'bg-red-50 text-red-600 border-red-100',
  delete_tapes_batch: 'bg-red-50 text-red-600 border-red-100',
  replace_tape: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  undo_replacement: 'bg-slate-100 text-slate-600 border-slate-200',
  flag_replacement: 'bg-amber-50 text-amber-700 border-amber-100',
  flag_damaged: 'bg-red-50 text-red-600 border-red-100',
  restore_tape: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  request_tapes: 'bg-amber-50 text-amber-700 border-amber-100',
  fulfill_request: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  cancel_request: 'bg-slate-100 text-slate-600 border-slate-200',
  create_account: 'bg-blue-50 text-blue-700 border-blue-100',
  update_account: 'bg-amber-50 text-amber-700 border-amber-100',
  reset_password: 'bg-amber-50 text-amber-700 border-amber-100',
  set_account_status: 'bg-slate-100 text-slate-600 border-slate-200',
  delete_account: 'bg-red-50 text-red-600 border-red-100',
};

function prettyAction(action: string): string {
  return ACTION_LABELS[action] ?? action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTime(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function csvEscape(v: string): string {
  if (/[",\n]/.test(v)) {
    return '"' + v.replace(/"/g, '""') + '"';
  }
  return v;
}

function xmlEscape(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function dateStamp(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function downloadFile(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function profileIsOwner(profile: { is_owner?: boolean } | null): boolean {
  return profile?.is_owner === true || Number(profile?.is_owner) === 1;
}

function logErrorMessage(error: unknown): string {
  const status = (error as { context?: Response })?.context?.status;
  if (status === 401) return '登录状态已失效，请重新登录';
  if (status === 403) return '只有 Owner 可以查看 Activity Log';
  return error instanceof Error ? error.message : '无法加载 Activity Log';
}

export default function LogsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [q, setQ] = useState('');
  const [fAction, setFAction] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(50);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1000);
      if (error) throw error;
      setLogs((data as ActivityLog[]) ?? []);
    } catch (e: any) {
      setLoadError(logErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!profile) return;
    if (!profileIsOwner(profile)) {
      setLoading(false);
      setLoadError('只有 Owner 可以查看 Activity Log');
      navigate('/', { replace: true });
      return;
    }
    void load();
  }, [profile, navigate, load]);

  const actions = useMemo(
    () => Array.from(new Set(logs.map((l) => l.action))),
    [logs],
  );

  const filtered = useMemo(
    () =>
      logs.filter((l) => {
        const hay = `${l.username ?? ''} ${l.user_email ?? ''} ${l.action ?? ''} ${l.details ?? ''} ${l.ip_address ?? ''}`.toLowerCase();
        if (q && !hay.includes(q.toLowerCase())) return false;
        if (fAction && l.action !== fAction) return false;
        if (fromDate || toDate) {
          const t = new Date(l.created_at).getTime();
          if (fromDate && t < new Date(`${fromDate}T00:00:00`).getTime()) return false;
          if (toDate && t > new Date(`${toDate}T23:59:59.999`).getTime()) return false;
        }
        return true;
      }),
    [logs, q, fAction, fromDate, toDate],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageData = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const exportCSV = () => {
    const header = ['Time', 'Username', 'Email', 'IP Address', 'Action', 'Details'];
    const lines = [header.join(',')];
    for (const l of filtered) {
      const row = [
        formatTime(l.created_at),
        l.username ?? '',
        l.user_email ?? '',
        l.ip_address ?? '',
        prettyAction(l.action),
        l.details ?? '',
      ];
      lines.push(row.map(csvEscape).join(','));
    }
    const csv = '\uFEFF' + lines.join('\r\n');
    downloadFile(`activity-log-${dateStamp()}.csv`, csv, 'text/csv;charset=utf-8;');
  };

  const exportExcel = () => {
    const header = ['Time', 'Username', 'Email', 'IP Address', 'Action', 'Details'];
    let html = '<table><thead><tr>';
    header.forEach((h) => { html += `<th>${xmlEscape(h)}</th>`; });
    html += '</tr></thead><tbody>';
    for (const l of filtered) {
      html += '<tr>';
      const row = [
        formatTime(l.created_at),
        l.username ?? '',
        l.user_email ?? '',
        l.ip_address ?? '',
        prettyAction(l.action),
        l.details ?? '',
      ];
      row.forEach((v) => { html += `<td>${xmlEscape(v)}</td>`; });
      html += '</tr>';
    }
    html += '</tbody></table>';
    const full = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="UTF-8"></head><body>${html}</body></html>`;
    downloadFile(`activity-log-${dateStamp()}.xls`, full, 'application/vnd.ms-excel');
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Activity Log</h1>
        <p className="text-sm text-slate-500 mt-1">
          Sign-in and operation history for all accounts, including IP address and time.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 mb-4 p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 flex items-center justify-center" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search user, email, IP, or details..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
          />
        </div>
        <div className="w-56">
          <select
            value={fAction}
            onChange={(e) => { setFAction(e.target.value); setPage(1); }}
            className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 bg-white cursor-pointer"
          >
            <option value="">All Actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>{prettyAction(a)}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
              className="text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 bg-white cursor-pointer"
              title="From date"
            />
          </div>
          <span className="text-slate-400 text-sm">—</span>
          <div className="relative">
            <input
              type="date"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPage(1); }}
              className="text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 bg-white cursor-pointer"
              title="To date"
            />
          </div>
        </div>
        {(q || fAction || fromDate || toDate) && (
          <button
            onClick={() => { setQ(''); setFAction(''); setFromDate(''); setToDate(''); }}
            className="text-xs text-slate-500 hover:text-slate-900 cursor-pointer whitespace-nowrap"
          >
            Clear filters
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={exportCSV}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-700 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-download-line w-4 h-4 flex items-center justify-center" />
            Export CSV
          </button>
          <button
            onClick={exportExcel}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-700 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-excel-2-line w-4 h-4 flex items-center justify-center" />
            Export Excel
          </button>
        </div>
      </div>

      {loadError && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
          <span>{loadError}</span>
          <button onClick={load} className="text-xs underline cursor-pointer">Retry</button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-100 bg-slate-50">
              <th className="px-4 py-3 font-medium">Time</th>
              <th className="px-4 py-3 font-medium">Account</th>
              <th className="px-4 py-3 font-medium">IP Address</th>
              <th className="px-4 py-3 font-medium">Action</th>
              <th className="px-4 py-3 font-medium">Details</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="text-center py-10 text-slate-400 text-sm">
                  Loading activity logs...
                </td>
              </tr>
            )}
            {!loading && pageData.map((l) => (
              <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs font-mono">
                  {formatTime(l.created_at)}
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800 whitespace-nowrap">{l.username || '—'}</div>
                  <div className="text-xs text-slate-400 whitespace-nowrap">{l.user_email || '—'}</div>
                </td>
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs font-mono">
                  {l.ip_address || '—'}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${
                      ACTION_COLORS[l.action] ?? 'bg-slate-100 text-slate-600 border-slate-200'
                    }`}
                  >
                    {prettyAction(l.action)}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.details || '—'}</td>
              </tr>
            ))}
            {!loading && pageData.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-10 text-slate-400 text-sm">
                  No activity found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500 flex-wrap gap-2">
            <span>
              {filtered.length} records · Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <span>Show</span>
                <select
                  value={String(pageSize)}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="text-xs px-2 py-1 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 bg-white cursor-pointer"
                >
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                </select>
                <span>per page</span>
              </label>
              <div className="flex gap-2">
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                  className="px-3 py-1 border border-slate-200 rounded-md disabled:opacity-40 cursor-pointer whitespace-nowrap"
                >
                  Prev
                </button>
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(currentPage + 1)}
                  className="px-3 py-1 border border-slate-200 rounded-md disabled:opacity-40 cursor-pointer whitespace-nowrap"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
